import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ApiRequestError, forgotPassword, getAuthConfig, login } from '../lib/api';
import { useSession } from '../lib/session';
import { setDesktopToken } from '../lib/runtime';
import { AuthShell, PasswordField } from '../components/AuthShell';

/**
 * A Microsoft-belepes visszateresenek hibakodjai (lasd szerver:
 * auth/routes.ts, `/auth/entra/callback`).
 */
const ENTRA_ERRORS: Record<string, string> = {
  entra_unknown_user:
    'Ehhez a Microsoft-fiókhoz nincs AVSlack-fiók. Kérj meghívást az adminisztrátortól.',
  entra_disabled_user: 'Ez a fiók le van tiltva.',
  entra_account_conflict:
    'Ez az AVSlack-fiók már egy másik Microsoft-fiókhoz van kötve. Szólj az adminisztrátornak.',
  entra_expired: 'A Microsoft-belépés túl sokáig tartott. Próbáld újra.',
  entra_failed: 'A Microsoft-belépés nem sikerült. Próbáld újra.',
};

/**
 * Bejelentkezes.
 *
 * Sajat felhasznalo-adatbazis: e-mail + jelszo. Az admin viszi fel a kollegat,
 * aki ideiglenes jelszot kap levelben, es az elso belepeskor ujat ad meg
 * (azt a kepernyot az `app-layout` kenyszeriti ki).
 *
 * Mellette - ha be van allitva - Microsoft-fiokkal is be lehet lepni
 * (versenyaudit B1). Ez csak a mar felvett kollegat engedi be: fiokot nem nyit,
 * es a kotelezo jelszocseret is atugorja (az identitast az Entra igazolta).
 */
export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [authEnabled, setAuthEnabled] = useState(true);
  const [entraEnabled, setEntraEnabled] = useState(false);

  // Elfelejtett jelszo panel
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Elo session eseten nincs mit keresni a login oldalon.
  useEffect(() => {
    if (session.data) void navigate({ to: '/' });
  }, [session.data, navigate]);

  useEffect(() => {
    void getAuthConfig()
      .then((config) => {
        setAuthEnabled(config.authEnabled);
        setEntraEnabled(Boolean(config.entraEnabled));
      })
      .catch(() => undefined);
  }, []);

  // A Microsoft-belepesbol hibakoddal jottunk vissza: kiirjuk, es eltuntetjuk a cimbol.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('hiba');
    if (!code) return;
    if (code !== 'entra_cancelled') setError(ENTRA_ERRORS[code] ?? ENTRA_ERRORS.entra_failed!);
    params.delete('hiba');
    const rest = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const result = await login(email.trim(), password);

      // Desktopban a tokent el kell tenni: ott nincs suti.
      if (result.token) setDesktopToken(result.token);

      await queryClient.invalidateQueries({ queryKey: ['session'] });
      void navigate({ to: '/' });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'A bejelentkezés nem sikerült.');
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  if (!authEnabled) {
    return (
      <AuthShell title="Fejlesztői mód">
        <div className="rounded-control border border-border-soft bg-surface-sunken px-3 py-3 text-sm">
          <p className="text-ink-muted">
            Az auth ki van kapcsolva (
            <code className="font-mono text-2xs">AVSLACK_AUTH_ENABLED=false</code>), minden keres a{' '}
            <strong className="text-ink">local</strong> fiókként fut.
          </p>
        </div>
      </AuthShell>
    );
  }

  if (forgotOpen) {
    return (
      <ForgotPanel
        initialEmail={email}
        sent={forgotSent}
        onSent={() => setForgotSent(true)}
        onBack={() => {
          setForgotOpen(false);
          setForgotSent(false);
        }}
      />
    );
  }

  return (
    <AuthShell title="AVSlack" subtitle="Alpha-Vet csapatkommunikáció">
      <form onSubmit={(event) => void submit(event)} className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-ink-muted">
            E-mail cím vagy felhasználónév
          </span>
          <input
            type="text"
            required
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="nev@alpha-vet.hu vagy kis.eva"
            className="w-full rounded-control border border-border bg-surface-raised px-3 py-2.5 text-sm"
          />
        </label>

        <PasswordField
          label="Jelszó"
          value={password}
          autoComplete="current-password"
          onChange={setPassword}
        />

        {error && (
          <p className="rounded-control border border-danger/30 bg-danger-soft px-3 py-2.5 text-sm text-danger">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy || !email.trim() || !password}
          className="w-full rounded-control bg-accent px-4 py-3 font-semibold text-accent-ink transition-all duration-(--duration-base) ease-(--ease-soft) hover:-translate-y-px hover:bg-accent-hover hover:shadow-(--shadow-lift) disabled:translate-y-0 disabled:opacity-50 disabled:shadow-none"
        >
          {busy ? 'Belépés…' : 'Belépés'}
        </button>
      </form>

      {entraEnabled && (
        <>
          <div className="my-4 flex items-center gap-3 text-2xs text-ink-faint">
            <span className="h-px flex-1 bg-border-soft" />
            vagy
            <span className="h-px flex-1 bg-border-soft" />
          </div>
          {/* Sima link: a teljes OIDC-tanc a szerveren zajlik, a bongeszo tokent nem lat. */}
          <a
            href="/auth/entra/login"
            className="flex w-full items-center justify-center gap-2.5 rounded-control border border-border bg-surface-raised px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-surface-hover"
          >
            <MicrosoftMark />
            Belépés Microsoft-fiókkal
          </a>
        </>
      )}

      <button
        type="button"
        onClick={() => setForgotOpen(true)}
        className="mt-4 block w-full text-center text-xs text-ink-muted underline transition-colors hover:text-ink"
      >
        Elfelejtettem a jelszavamat
      </button>
    </AuthShell>
  );
}

/** A Microsoft negy negyzete - a felismeres miatt, nem dekoracio. */
function MicrosoftMark() {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16">
      <rect x="0" y="0" width="7.5" height="7.5" fill="#f25022" />
      <rect x="8.5" y="0" width="7.5" height="7.5" fill="#7fba00" />
      <rect x="0" y="8.5" width="7.5" height="7.5" fill="#00a4ef" />
      <rect x="8.5" y="8.5" width="7.5" height="7.5" fill="#ffb900" />
    </svg>
  );
}

/**
 * Elfelejtett jelszo.
 *
 * A visszajelzes MINDIG ugyanaz, letezik-e a cim vagy sem: kulonben ez a
 * kepernyo egy kenyelmes e-mail-ellenorzo szolgaltatas lenne ("dolgozik-e
 * meg naluk ez az ember?").
 */
function ForgotPanel({
  initialEmail,
  sent,
  onSent,
  onBack,
}: {
  initialEmail: string;
  sent: boolean;
  onSent: () => void;
  onBack: () => void;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    await forgotPassword(email.trim()).catch(() => undefined);
    setBusy(false);
    onSent();
  };

  return (
    <AuthShell title="Új jelszó" subtitle="Küldünk egy linket, amivel beállíthatod">
      {sent ? (
        <div className="space-y-3 text-sm">
          <p className="rounded-control border border-border-soft bg-surface-sunken px-3 py-3 text-ink-muted">
            Ha ezzel a címmel van fiók, elküldtük a linket. Nézd meg a postafiókod — a link egy
            óráig érvényes.
          </p>
          <button
            type="button"
            onClick={onBack}
            className="w-full rounded-control bg-accent px-4 py-2.5 text-sm font-semibold text-accent-ink"
          >
            Vissza a belépéshez
          </button>
        </div>
      ) : (
        <form onSubmit={(event) => void submit(event)} className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-ink-muted">E-mail cím</span>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="név@alpha-vet.hu"
              className="w-full rounded-control border border-border bg-surface-raised px-3 py-2.5 text-sm"
            />
          </label>

          <button
            type="submit"
            disabled={busy || !email.trim()}
            className="w-full rounded-control bg-accent px-4 py-2.5 font-semibold text-accent-ink disabled:opacity-50"
          >
            {busy ? 'Küldés…' : 'Link kérése'}
          </button>

          <button
            type="button"
            onClick={onBack}
            className="w-full text-center text-xs text-ink-muted underline hover:text-ink"
          >
            Mégse
          </button>
        </form>
      )}
    </AuthShell>
  );
}
